import { z } from 'zod';

import { VALIDATION_MESSAGES } from '@/constants/constant';
import { ECurrency } from '@/enum';

export const STUDENT_LIMIT_MAX = 100000;
export const SUBSCRIPTION_AMOUNT_MAX = 1000000;
export const SUBSCRIPTION_INPUT_MAX_LENGTH = 10;

export const subscriptionFields = {
  studentLimit: z
    .number({
      error: VALIDATION_MESSAGES.scheduleRenewal.studentLimit.required,
    })
    .int(VALIDATION_MESSAGES.scheduleRenewal.studentLimit.invalid)
    .min(1, VALIDATION_MESSAGES.scheduleRenewal.studentLimit.min)
    .max(
      STUDENT_LIMIT_MAX,
      VALIDATION_MESSAGES.scheduleRenewal.studentLimit.max
    ),

  amount: z
    .number({ error: VALIDATION_MESSAGES.scheduleRenewal.amount.required })
    .positive(VALIDATION_MESSAGES.amount.invalid)
    .max(
      SUBSCRIPTION_AMOUNT_MAX,
      VALIDATION_MESSAGES.scheduleRenewal.amount.max
    )
    .refine((value) => {
      const decimalPlaces = value.toString().split('.')[1]?.length ?? 0;
      return decimalPlaces <= 3;
    }, VALIDATION_MESSAGES.amount.invalid),

  currency: z.enum(ECurrency, {
    error: VALIDATION_MESSAGES.scheduleRenewal.currency.required,
  }),

  subscriptionStartDate: z
    .string()
    .nonempty(VALIDATION_MESSAGES.subscriptionStartDate.required),

  subscriptionEndDate: z
    .string()
    .nonempty(VALIDATION_MESSAGES.subscriptionEndDate.required),

  transactionId: z
    .string()
    .trim()
    .nonempty(VALIDATION_MESSAGES.transactionId.required)
    .min(3, VALIDATION_MESSAGES.transactionId.min)
    .max(50, VALIDATION_MESSAGES.transactionId.max)
    .regex(/^[A-Za-z0-9_-]+$/, VALIDATION_MESSAGES.transactionId.invalid),
};

export const subscriptionDateRangeRefine = (
  data: { subscriptionStartDate?: string; subscriptionEndDate?: string },
  ctx: z.RefinementCtx
) => {
  if (
    data.subscriptionStartDate &&
    data.subscriptionEndDate &&
    data.subscriptionEndDate <= data.subscriptionStartDate
  ) {
    ctx.addIssue({
      code: 'custom',
      path: ['subscriptionEndDate'],
      message: VALIDATION_MESSAGES.subscriptionEndDate.beforeStart,
    });
  }
};

export const scheduleRenewalSchema = z
  .object({
    studentLimit: subscriptionFields.studentLimit,
    amount: subscriptionFields.amount,
    currency: subscriptionFields.currency,
    subscriptionStartDate: subscriptionFields.subscriptionStartDate,
    subscriptionEndDate: subscriptionFields.subscriptionEndDate,
    transactionId: subscriptionFields.transactionId,
  })
  .superRefine(subscriptionDateRangeRefine);

export type TScheduleRenewalFormValues = z.infer<typeof scheduleRenewalSchema>;

export const editSubscriptionSchema = z.object({
  studentLimit: subscriptionFields.studentLimit,
  amount: subscriptionFields.amount,
  currency: subscriptionFields.currency,
  transactionId: subscriptionFields.transactionId,
});

export type TEditSubscriptionFormValues = z.infer<
  typeof editSubscriptionSchema
>;
